import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@/components/common/PagedTableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { apiError, useUsers } from '@/api/admin';
import { useAuthStore } from '@/auth/authStore';
import { useParties } from '../api/operations';
import { LICENCE_KINDS, METHODS, useOrderCheck, useOrderExemptions, useSourcingActions, useSourcingEvents, useSupplierLicences, type LicenceKind, type SourcingEvent, type SourcingMethod } from '../api/sourcing';

const day = (offset: number) => new Date(Date.now() + offset * 86400000).toISOString().slice(0, 10);

/** Each supplier's licences, nearest expiry first (docs §295; PRC-008). */
export function LicencesView({ canConfigure }: { canConfigure: boolean }) {
  const { data, error } = useSupplierLicences();
  const { data: exemptions } = useOrderExemptions();
  const { data: suppliers } = useParties('suppliers', { page: 1, pageSize: 200 });
  const { saveLicence, removeLicence } = useSourcingActions();
  const [f, setF] = useState({ supplierId: '', kind: 'PharmacyRegulator' as LicenceKind, number: '', expiresOn: '' });
  const [message, setMessage] = useState('');
  return (
    <Box>
      {!!error && <Alert severity="error" sx={{ mb: 2 }}>{apiError(error)}</Alert>}
      {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}
      {canConfigure && (
        <Stack direction="row" gap={1} flexWrap="wrap" mb={2}>
          <TextField select size="small" label="Supplier" value={f.supplierId} onChange={(e) => setF({ ...f, supplierId: e.target.value })} sx={{ minWidth: 240 }}>{(suppliers?.items ?? []).map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}</TextField>
          <TextField select size="small" label="Kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as LicenceKind })} sx={{ minWidth: 180 }}>{LICENCE_KINDS.map((k) => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}</TextField>
          <TextField size="small" label="Number" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} />
          <TextField size="small" type="date" label="Expires" value={f.expiresOn} onChange={(e) => setF({ ...f, expiresOn: e.target.value })} InputLabelProps={{ shrink: true }} />
          <Button size="small" variant="contained" disabled={!f.supplierId || !f.number.trim()} onClick={() => { setMessage(''); saveLicence.mutateAsync({ id: null, supplierId: f.supplierId, kind: f.kind, number: f.number, issuedOn: null, expiresOn: f.expiresOn || null, notes: null }).then(() => setF({ ...f, number: '', expiresOn: '' })).catch((e: unknown) => setMessage(apiError(e))); }}>Add</Button>
        </Stack>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow><TableCell>Supplier</TableCell><TableCell>Kind</TableCell><TableCell>Number</TableCell><TableCell>Expires</TableCell>{canConfigure && <TableCell />}</TableRow></TableHead>
          <TableBody>
            {(data ?? []).map((l) => (
              <TableRow key={l.id}>
                <TableCell>{l.supplierName}</TableCell>
                <TableCell>{LICENCE_KINDS.find((k) => k.value === l.kind)?.label}</TableCell>
                <TableCell>{l.number}</TableCell>
                <TableCell>{l.expiresOn ?? 'No expiry'}{l.expired ? <Chip size="small" color="error" label="Expired" sx={{ ml: 1 }} /> : l.daysLeft != null && l.daysLeft <= 60 ? <Chip size="small" color="warning" label={`${l.daysLeft} days`} sx={{ ml: 1 }} /> : null}</TableCell>
                {canConfigure && <TableCell align="right"><Button size="small" color="error" onClick={() => { setMessage(''); removeLicence.mutateAsync(l.id).catch((e: unknown) => setMessage(apiError(e))); }}>Remove</Button></TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {(exemptions ?? []).length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>Items exempted from registration</Typography>
          {exemptions!.map((e) => <Typography key={e.id} variant="body2">{e.itemCode} {e.itemName}, until {e.validUntil}: {e.reason} ({e.approvedBy})</Typography>)}
        </Box>
      )}
    </Box>
  );
}

/** Shown on a draft order: what would stop it being issued, and the approval of a price that differs from the framework. */
export function OrderChecks({ id, canApprove }: { id: string; canApprove: boolean }) {
  const { data } = useOrderCheck(id, true);
  const { approvePrice } = useSourcingActions();
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  if (!data || (data.refusals.length === 0 && data.lines.length === 0)) return null;
  return (
    <Stack gap={1} sx={{ my: 1 }}>
      {data.refusals.map((r) => <Alert key={r} severity="warning">{r}</Alert>)}
      {data.lines.map((l) => <Typography key={l.lineNo} variant="body2">Line {l.lineNo}: {l.unitPrice} against {l.frameworkPrice} on {l.agreement}</Typography>)}
      {data.approvedBy && <Typography variant="body2">Accepted by {data.approvedBy}: {data.approvedReason}</Typography>}
      {message && <Alert severity="error">{message}</Alert>}
      {data.lines.length > 0 && !data.approvedBy && canApprove && (
        <Stack direction="row" gap={1}>
          <TextField size="small" label="Why the price may differ" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button size="small" disabled={!reason.trim()} onClick={() => { setMessage(''); approvePrice.mutateAsync({ id, reason }).catch((e: unknown) => setMessage(apiError(e))); }}>Accept the price</Button>
        </Stack>
      )}
    </Stack>
  );
}

/** Tenders and quotations (docs §295; PRC-005, PRC-006): a committee declares before it scores, and an award against the marks says why. */
export function TendersView({ canCapture }: { canCapture: boolean }) {
  const { data, error } = useSourcingEvents();
  const [creating, setCreating] = useState(false);
  return (
    <Box>
      {!!error && <Alert severity="error" sx={{ mb: 2 }}>{apiError(error)}</Alert>}
      {canCapture && <Button size="small" variant="outlined" sx={{ mb: 2 }} onClick={() => setCreating(!creating)}>{creating ? 'Close' : 'New tender'}</Button>}
      {creating && <NewTender onDone={() => setCreating(false)} />}
      <Stack gap={2}>
        {(data ?? []).map((e) => <TenderCard key={e.id} event={e} canCapture={canCapture} />)}
        {data?.length === 0 && <Typography color="text.secondary">No tenders yet.</Typography>}
      </Stack>
    </Box>
  );
}

function NewTender({ onDone }: { onDone: () => void }) {
  const { data: users } = useUsers();
  const { create } = useSourcingActions();
  const [f, setF] = useState({ title: '', method: 'OpenTender' as SourcingMethod, closesOn: day(21), why: '', criteria: 'Price:60, Quality:40', members: [] as string[] });
  const [message, setMessage] = useState('');
  const criteria = f.criteria.split(',').map((c) => c.split(':')).filter((c) => c.length === 2).map(([name, weight]) => ({ name: name.trim(), weight: Number(weight) }));
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack gap={1}>
          {message && <Alert severity="error">{message}</Alert>}
          <TextField size="small" label="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <TextField select size="small" label="Method" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value as SourcingMethod })} sx={{ minWidth: 220 }}>{METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}</TextField>
            <TextField size="small" type="date" label="Closes" value={f.closesOn} onChange={(e) => setF({ ...f, closesOn: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Stack>
          {(f.method === 'Direct' || f.method === 'Restricted') && <TextField size="small" label="Why not open competition" value={f.why} onChange={(e) => setF({ ...f, why: e.target.value })} />}
          {f.method !== 'Direct' && <TextField size="small" label="Criteria and weights" helperText="Name:weight, adding up to 100" value={f.criteria} onChange={(e) => setF({ ...f, criteria: e.target.value })} />}
          {f.method !== 'Direct' && <TextField select size="small" label="Committee" SelectProps={{ multiple: true, value: f.members, onChange: (e) => setF({ ...f, members: e.target.value as string[] }) }}>{(users ?? []).map((u) => <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>)}</TextField>}
          <Box><Button size="small" variant="contained" disabled={!f.title.trim()} onClick={() => { setMessage(''); create.mutateAsync({ title: f.title, method: f.method, closesOn: f.closesOn, requisitionId: null, justification: f.why || null, criteria: f.method === 'Direct' ? [] : criteria, memberIds: f.members }).then(onDone).catch((e: unknown) => setMessage(apiError(e))); }}>Create</Button></Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function TenderCard({ event: e, canCapture }: { event: SourcingEvent; canCapture: boolean }) {
  const { data: suppliers } = useParties('suppliers', { page: 1, pageSize: 200 });
  const myId = useAuthStore((s) => s.user?.id);
  const { open, addBid, close, declare, score, award } = useSourcingActions();
  const me = e.members.find((m) => m.userId === myId);
  const [bid, setBid] = useState({ supplierId: '', amount: '' });
  const [conflict, setConflict] = useState({ has: false, note: '' });
  const [marks, setMarks] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const act = (run: () => Promise<unknown>) => { setMessage(''); run().catch((x: unknown) => setMessage(apiError(x))); };
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" mb={1}>
          <Typography fontWeight={600}>{e.title}</Typography>
          <Chip size="small" label={e.status} />
          <Typography variant="caption" color="text.secondary">{e.number}, {METHODS.find((m) => m.value === e.method)?.label}, closes {e.closesOn}</Typography>
        </Stack>
        {e.justification && <Typography variant="body2" color="text.secondary">{e.justification}</Typography>}
        {message && <Alert severity="error" sx={{ my: 1 }}>{message}</Alert>}
        {e.members.length > 0 && <Typography variant="body2">Committee: {e.members.map((m) => `${m.name}${m.hasConflict ? ' (conflict declared)' : m.declaredAt ? '' : ' (not declared)'}`).join(', ')}</Typography>}
        <TableContainer sx={{ my: 1 }}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Bidder</TableCell><TableCell align="right">Amount</TableCell><TableCell align="right">Score</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {e.bids.map((b) => (
                <TableRow key={b.id}>
                  <TableCell>{b.supplierName}{b.id === e.awardedBidId && <Chip size="small" color="success" label="Awarded" sx={{ ml: 1 }} />}{b.disqualified && <Chip size="small" color="error" label="Disqualified" sx={{ ml: 1 }} />}</TableCell>
                  <TableCell align="right">{b.amount.toLocaleString()}</TableCell>
                  <TableCell align="right">{b.score ?? ''}</TableCell>
                  <TableCell align="right">{e.status === 'Closed' && !b.disqualified && canCapture && <Button size="small" onClick={() => act(() => award.mutateAsync({ id: e.id, bidId: b.id, reason: reason || null }))}>Award</Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {e.status === 'Draft' && canCapture && <Button size="small" onClick={() => act(() => open.mutateAsync(e.id))}>Open for bids</Button>}
        {e.status === 'Open' && canCapture && (
          <Stack direction="row" gap={1} flexWrap="wrap">
            <TextField select size="small" label="Supplier" value={bid.supplierId} onChange={(x) => setBid({ ...bid, supplierId: x.target.value })} sx={{ minWidth: 220 }}>{(suppliers?.items ?? []).map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}</TextField>
            <TextField size="small" type="number" label="Amount" value={bid.amount} onChange={(x) => setBid({ ...bid, amount: x.target.value })} />
            <Button size="small" disabled={!bid.supplierId || !bid.amount} onClick={() => act(() => addBid.mutateAsync({ id: e.id, supplierId: bid.supplierId, amount: Number(bid.amount), leadTimeDays: null }))}>Record bid</Button>
            <Button size="small" onClick={() => act(() => close.mutateAsync(e.id))}>Close bidding</Button>
          </Stack>
        )}
        {e.status === 'Closed' && me && !me.declaredAt && (
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <FormControlLabel label="I have a conflict of interest" control={<Checkbox size="small" checked={conflict.has} onChange={(x) => setConflict({ ...conflict, has: x.target.checked })} />} />
            {conflict.has && <TextField size="small" label="What it is" value={conflict.note} onChange={(x) => setConflict({ ...conflict, note: x.target.value })} />}
            <Button size="small" onClick={() => act(() => declare.mutateAsync({ id: e.id, hasConflict: conflict.has, note: conflict.note || null }))}>Declare</Button>
          </Stack>
        )}
        {e.status === 'Closed' && me?.declaredAt && !me.hasConflict && (
          <Box mt={1}>
            <Typography variant="subtitle2">Your marks, out of 100</Typography>
            {e.bids.filter((b) => !b.disqualified).map((b) => (
              <Stack key={b.id} direction="row" gap={1} alignItems="center" flexWrap="wrap" mt={1}>
                <Typography variant="body2" sx={{ minWidth: 180 }}>{b.supplierName}</Typography>
                {e.criteria.map((c) => <TextField key={c.id} size="small" type="number" label={`${c.name} (${c.weight}%)`} value={marks[`${b.id}:${c.id}`] ?? ''} onChange={(x) => setMarks({ ...marks, [`${b.id}:${c.id}`]: x.target.value })} sx={{ width: 140 }} />)}
              </Stack>
            ))}
            <Button size="small" sx={{ mt: 1 }} onClick={() => act(() => score.mutateAsync({ id: e.id, scores: Object.entries(marks).map(([k, v]) => ({ bidId: k.split(':')[0], criterionId: k.split(':')[1], score: Number(v) })) }))}>Save marks</Button>
          </Box>
        )}
        {e.status === 'Closed' && canCapture && <TextField size="small" fullWidth label="Reason, if the award is not the highest score" value={reason} onChange={(x) => setReason(x.target.value)} sx={{ mt: 1 }} />}
        {e.awardReason && <Typography variant="body2" mt={1}>Awarded by {e.awardedBy}: {e.awardReason}</Typography>}
      </CardContent>
    </Card>
  );
}
